import React from 'react'
import Helmet from 'react-helmet'
import AddCircle from '@material-ui/icons/AddCircle';
import Select from '@material-ui/core/Select'
import Switch from '@material-ui/core/Switch'
import Slider from '@material-ui/lab/Slider'
import { constrain } from '../../utils';
import ColorPicker from '../../shared/colorPicker/colorPicker';

const ACCEPTED_CUSTOM_FONT_EXTENSIONS = [
  'woff2',
  'woff',
  'ttf',
  'otf',
];

const FONT_WEIGHTS = [
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
];

const GOOGLE_FONT_API_KEY = 'AIzaSyBaAAg-pUU2lWaNum1dyBEhWjXNp0uE818';

export default class FontEditor extends React.Component {
  constructor() {
    super();
    this.state = { allFonts: [] };
  }

  componentDidMount() {
    fetch(
      `https://www.googleapis.com/webfonts/v1/webfonts?key=${GOOGLE_FONT_API_KEY}&sort=popularity`
    )
      .then(res => res.json())
      .then(json => this.parseFontOptions(json))
      .catch(e => console.log(e))
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { fontOptions } = this.props;
    const { allFonts } = this.state;

    // google fonts have loaded
    if (nextState.allFonts.length !== allFonts.length) {
      return true;
    }

    // fontOptions object update timestamp has updated
    if (nextProps.fontOptions.lastUpdate === fontOptions.lastUpdate) {
      return false;
    }

    return true;
  }

  onFontChange = fontFamily => {
    const { onChange, fontOptions } = this.props
    const fontToFetch = `${fontFamily}:${fontOptions.fontWeight}`

    window.WebFont.load({
      google: {
        families: [fontToFetch],
      },
    })

    onChange(fontFamily, 'family');
  }

  onFontWeightChange = fontWeight => {
    const { onChange, fontOptions } = this.props
    const fontToFetch = `${fontOptions.family}:${fontWeight}`

    window.WebFont.load({
      google: {
        families: [fontToFetch],
      },
    })

    onChange(fontWeight, 'fontWeight');
  }

  onFileInputChange = (e) => {
    const {onChange} = this.props;

    const fileReader = new FileReader();
    const file = this.fileInput.files[0];
  
    if(!file) return; // No file input

    const splitFileName = file.name.split(".");
    const fileExtension = splitFileName[splitFileName.length - 1];

    if(!ACCEPTED_CUSTOM_FONT_EXTENSIONS.includes(fileExtension)) {
      alert("Not an accepted font type");
      return; // Not an accepted extension
    } 
    
    // TODO add progress event listener and animation when loading

    fileReader.readAsArrayBuffer(file);
    fileReader.onload = () => {
      const loadedFontName = splitFileName[0];
      const loadedFont = new FontFace(loadedFontName, fileReader.result);

      loadedFont.load().then((loadedFontFace) => {
        document.fonts.add(loadedFontFace);  
        onChange(loadedFontName, 'customFont')

      }).catch((error) => {
        console.log(error);
      });
    };
  }

  parseFontOptions = fontJson => {
    const parsedOptions = (fontJson.items || []).map(font => font.family)
    this.setState({ allFonts: parsedOptions })
  }

  render() {
    const { allFonts } = this.state
    const { onChange, fontOptions } = this.props
    
    return (
      <>
        <Helmet>
          <script
            type="text/javascript"
            src="https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js"
          />
        </Helmet>

        <div className="edit-field-section">
        
          <div className="edit-field">
            <div className="edit-field-title">Font</div>
            <div className="edit-field-value">
              <Select
                className="select-field"
                classes={{root: 'select-field__root', select: 'select-field__select'}}
                native
                value={fontOptions.family}
                onChange={e => {
                  this.onFontChange(e.target.value)
                }}                
              >
                <option value="aktiv-grotesk-medium">Aktiv Grotesk</option>
                {allFonts.map(option => {
                  return (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  )
                })}
              </Select>
            </div>
          </div>

          <div className="edit-field">
            <div className="edit-field-title">Custom Font</div>
            <div className="edit-field-value">
              <div className="custom-font-overlay">
                {!fontOptions.customFont && <AddCircle />}
                {!fontOptions.customFont && <span className="custom-font-message">* .ttf, .woff2, .woff, .otf</span>}
                {fontOptions.customFont && <span className="custom-font-active-title">{fontOptions.customFont}</span>}
              </div>
              <input className="custom-font-input" type="file" onChange={this.onFileInputChange} ref={r => {this.fileInput = r;}} />
            </div>
          </div>          

          <div className="edit-field-group">

            <div className="edit-field half" >
              <div className="edit-field-title">Size</div>
              <div className="edit-field-value">
                <div className="edit-field-input">
                  <input 
                    type="number"
                    className="value"
                    min={0}
                    max={150}
                    value={fontOptions.size}
                    onChange={e => {onChange(constrain(e.currentTarget.value, 0, 150), 'size')}}
                  />
                  <div className="unit">px</div>
                </div>
                <Slider
                  value={fontOptions.size}
                  min={0}
                  max={150}                
                  onChange={(e, sliderValue) => {
                    onChange(Math.round(sliderValue), 'size')
                  }}
                />
              </div>
            </div>

            <div className="edit-field half">
              <div className="edit-field-title">Weight</div>
              <div className="edit-field-value">
                <Select
                  className="select-field"
                  classes={{root: 'select-field__root', select: 'select-field__select'}}
                  native
                  value={fontOptions.fontWeight}
                  onChange={e => {
                    this.onFontWeightChange(e.target.value)
                  }}
                >
                  {FONT_WEIGHTS.map(option => {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    )
                  })}
                </Select>
              </div>
            </div>

            <div className="edit-field half">
              <div className="edit-field-title">Letter Spacing</div>
              <div className="edit-field-value">
                <div className="edit-field-input">
                    <input 
                      type="number"
                      className="value"
                      min={-.2}
                      max={.2}
                      value={fontOptions.letterSpacing}
                      onChange={e => {onChange(constrain(e.currentTarget.value, -.2, .2), 'letterSpacing')}}
                    />
                    <div className="unit">em</div>
                  </div>
                  <Slider
                    value={fontOptions.letterSpacing * 10000}
                    min={-2000}
                    max={2000}
                    step={50}              
                    onChange={(e, sliderValue) => {
                      onChange(sliderValue / 10000, 'letterSpacing')
                    }}
                  />
                </div>
            </div>
      
            <div className="edit-field half">
              <div className="edit-field-title">Vertical</div>
              <div className="edit-field-value">
                <div className="edit-field-input">
                  <input 
                    type="number"
                    className="value"
                    min={0}
                    max={200}
                    value={fontOptions.lineHeight}
                    onChange={e => {onChange(constrain(e.currentTarget.value, 0, 200), 'lineHeight')}}
                  />
                  <div className="unit">px</div>
                </div>

                <Slider
                  value={fontOptions.lineHeight}
                  min={0}
                  max={200}
                  step={1}                
                  onChange={(e, sliderValue) => {
                    onChange(sliderValue, 'lineHeight')
                  }}
                />
                
              </div>
            </div>

          </div>

      
          <div className="edit-field half">
            <div className="edit-field-title">Max Width</div>
            <div className="edit-field-value">
              <div className="edit-field-input">
                <input 
                  type="number"
                  className="value"
                  min={50}
                  max={1140}
                  value={fontOptions.maxWidth}
                  onChange={e => {onChange(constrain(e.currentTarget.value, 50, 1140), 'maxWidth')}}
                />
                <div className="unit">px</div>
              </div>
              <Slider
                value={fontOptions.maxWidth}
                min={50}
                max={1140}
                step={5}
                onChange={(e, sliderValue) => {
                  onChange(sliderValue, 'maxWidth')
                }}
              />
            </div>
          </div>

          <div className="edit-field">
            <div className="edit-field-title">Underline</div>
            <div className="edit-field-value">
              <Switch
                checked={fontOptions.underline}
                onChange={(e, value) => onChange(value, 'underline')}
              />
            </div>
          </div>

          <div className="edit-field">
            <div className="edit-field-title">Color</div>
            <div className="edit-field-value">        
                <ColorPicker
                  color={fontOptions.color}
                  onChange={onChange}
                />
            </div>
          </div>
        </div>
      </>
    )
  }
}
